/**
 * @fileoverview More detail dialog component for displaying comprehensive order information.
 * Provides a detailed view of order properties and additional data not shown
 * in the main order grid interface.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import React, { useState } from 'react';
import { useNotify } from 'react-admin';
import { Grid, Typography } from '@mui/material';
import MDialog from '../../../../../components/controls/MDialog';
import LoadingButton from '../../../../../components/controls/LoadingButton';
import { List } from '@mui/icons-material';
import { IData } from '../../../../../types';

/**
 * Material-UI label component for displaying name-value pairs in a formatted grid.
 * @param props - `name` (label to display) and `value` (value to display next to the label).
 */
const MLabel = ({ name, value }: any) => {
    return (
        <Grid container spacing={2} style={{ marginTop: '5px', marginBottom: '5px' }}>
            <Grid size={3}>
                <Typography style={{ fontWeight: 'bold', textAlign: 'right' }}>{name}</Typography>
            </Grid>
            <Grid size={9}>
                <Typography>{value}</Typography>
            </Grid>
        </Grid>
    );
};

/** Props for {@link MoreDetailPopup}. */
interface MoreDetailProps {
    /** Vendor configuration object. */
    vendor: any;
    /** Data provider for API operations. */
    dataProvider: any;
    /** Data containing order information. */
    data: IData;
}

/**
 * More detail popup component for displaying additional order/customer information.
 * @param props - See {@link MoreDetailProps}.
 */
const MoreDetailPopup: React.FC<MoreDetailProps> = ({ vendor, dataProvider, data }) => {
    const { order } = data;
    const [item, setItem] = useState<IData>({});
    const [isOpen, setOpen] = useState(false);
    const notify = useNotify();

    async function doMore () {
        let response = await dataProvider.fetchData(vendor.controller, `GetCustomerDetail?orderNumber=${order.order_number}`);
        if (response) {
            let t = response.data;
            if (t) {
                if (!t.order) {
                    notify('Order is not existing or error.', { type: 'error' });
                    return;
                }
                let fullname = t.order.firstName;
                if (t.order.lastName) fullname += ' ' + t.order.lastName;
                let data = {
                    order_number: order.order_number,
                    event: t.order.event,
                    venue: t.order.venue,
                    notes: t.order.notes,
                    eticket: t.order.electronicDelivery,
                    airbill: t.order.airBillLink,
                    firstname: t.order.firstName,
                    lastname: t.order.lastName,
                    fullname: fullname,
                    email: t.order.email
                };
                setItem(data);
                setOpen(true);
            }
        }
    }

    return (
        <>
            <LoadingButton
                size="small"
                startIcon={<List />}
                onClick={doMore}
            >
                More ...
            </LoadingButton>
            {isOpen && (
                <MDialog
                    className={'order-dialog'}
                    title={`Order #${order.order_number}`}
                    maxWidth={'sm'}
                    name={'isOpen'}
                    open={isOpen}
                    handleClose={() => setOpen(false)}
                >
                    <Grid container style={{ marginTop: '5px', marginBottom: '5px' }}>
                        <Typography style={{ fontWeight: 'bold', fontSize: 20 }}>Event info</Typography>
                    </Grid>
                    <MLabel name={'Event'} value={item.event} />
                    <MLabel name={'Venue'} value={item.venue} />
                    <Grid container style={{ marginTop: '20px', marginBottom: '5px' }}>
                        <Typography style={{ fontWeight: 'bold', fontSize: 20 }}>Customer info</Typography>
                    </Grid>
                    <MLabel name={'Name'} value={item.fullname} />
                    <MLabel name={'Email'} value={item.email} />
                </MDialog>
            )}
        </>
    );
};

export default MoreDetailPopup;
